/**
 * @file useBottomObstacle.ts
 * @description Место кнопки чата над нижними полосами действий
 * (chat-window-design.md §1а, §6 «Кнопка и нижние полосы»).
 *
 * Базово кнопка стоит в 24 от края (от 1024) или в 16 + безопасная зона (до
 * 1024). Видимые полосы с меткой `data-wms-sticky-bar` (`CabinetBottomBar`,
 * `StockSelectionBar`, `BinSelectionBar` …), которые заходят под кнопку,
 * поднимают её: низ кнопки = высота экрана − верх полосы + 12. Полосы бывают
 * `sticky` и двигаются при прокрутке — пересчёт по прокрутке, размеру окна и
 * изменениям DOM (не чаще кадра). На телефоне с открытой клавиатурой кнопка
 * прячется: поле ввода важнее.
 */

import { useEffect, useState } from "react";

import { STICKY_BAR_ATTR } from "../ui/sticky-bar";

/** Размер кнопки и зазор над полосой. */
export const FAB_SIZE = 56;
const BAR_GAP = 12;

/**
 * Нижний отступ кнопки по полосам на экране. Чистая функция: на вход —
 * коробки полос, горизонталь кнопки и базовый отступ.
 */
export function fabBottom(
  bars: ReadonlyArray<Pick<DOMRect, "top" | "left" | "right" | "height">>,
  viewportHeight: number,
  fab: { left: number; right: number },
  base: number,
): number {
  let bottom = base;
  for (const bar of bars) {
    if (bar.height <= 0) continue;
    // Полоса не под кнопкой по горизонтали — не мешает.
    if (bar.right <= fab.left || bar.left >= fab.right) continue;
    // Полоса ниже кнопки при базовом отступе — тоже не мешает.
    if (bar.top >= viewportHeight - base) continue;
    bottom = Math.max(bottom, viewportHeight - bar.top + BAR_GAP);
  }
  return bottom;
}

/** Клавиатура телефона открыта: видимая область заметно ниже окна. */
function keyboardOpen(): boolean {
  const vv = window.visualViewport;
  return !!vv && vv.height < window.innerHeight * 0.75;
}

export interface BottomObstacle {
  /** Нижний отступ кнопки, px (без безопасной зоны — её добавляет вёрстка). */
  bottom: number;
  /** Отступ задан полосой (безопасная зона уже учтена её высотой). */
  raised: boolean;
  /** Спрятать кнопку (клавиатура открыта). */
  hidden: boolean;
}

export function useBottomObstacle(enabled: boolean, wide: boolean): BottomObstacle {
  const base = wide ? 24 : 16;
  const [value, setValue] = useState<BottomObstacle>({ bottom: base, raised: false, hidden: false });

  useEffect(() => {
    if (!enabled) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const right = window.innerWidth - base;
      const bars = [...document.querySelectorAll<HTMLElement>(`[${STICKY_BAR_ATTR}]`)].map((el) => el.getBoundingClientRect());
      const bottom = fabBottom(bars, window.innerHeight, { left: right - FAB_SIZE, right }, base);
      const hidden = !wide && keyboardOpen();
      setValue((prev) =>
        prev.bottom === bottom && prev.hidden === hidden ? prev : { bottom, raised: bottom !== base, hidden },
      );
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    measure();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    window.visualViewport?.addEventListener("resize", schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
      window.visualViewport?.removeEventListener("resize", schedule);
    };
  }, [enabled, wide, base]);

  return value;
}
